import Modal from '@/components/Modal/Modal';
import { Button } from "@mui/material";
import FormControl from "@/components/FormControl/FormControl";
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { useEffect, useState, memo } from 'react';
import type { AppDispatch } from '@/store/store';
import { getGeoFencingArea } from '@/store/slices/teamSlice';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import Alert from '@mui/material/Alert';

interface IManageSelectedUsersGeofences {
    modalOpen: boolean;
    closeModal: () => void;
    saveModal: (data: { mode: "add" | "remove"; payload?: any }) => void;
    selectedIds: any[];
    mode: "add" | "remove";
    usersList: any[];
    applyToAllUsers?: boolean;
    filters?: any; // The active filter-search
}

const ManageSelectedUsersGeofences = ({
    modalOpen,
    closeModal,
    saveModal,
    selectedIds,
    mode,
    usersList,
    applyToAllUsers = false,
    filters = null,
}: IManageSelectedUsersGeofences) => {
    const [selectedAction, setSelectedAction] = useState<"PunchIn" | "PunchOut" | null>(null);

    const { geoFencingArea } = useSelector((state: any) => state.team);
    const { loading } = useSelector((state: any) => state.usergeofenceManagement);

    const { control, watch, reset, setValue } = useForm({
        defaultValues: {
            punchInGeoFenceIds: [] as any[],
            punchOutGeoFenceIds: [] as any[],
        }
    });

    const dispatch = useDispatch<AppDispatch>();

    const punchInGeoFenceIds = watch("punchInGeoFenceIds") || [];
    const punchOutGeoFenceIds = watch("punchOutGeoFenceIds") || [];

    const selectedPunchInGeofences = (geoFencingArea || []).filter((g: any) =>
        punchInGeoFenceIds.includes(g.Id)
    );

    const selectedPunchOutGeofences = (geoFencingArea || []).filter((g: any) =>
        punchOutGeoFenceIds.includes(g.Id)
    );

    const handleRemovePunchIn = (id: any) => {
        setValue("punchInGeoFenceIds", punchInGeoFenceIds.filter((x: any) => x !== id));
    };

    const handleRemovePunchOut = (id: any) => {
        setValue("punchOutGeoFenceIds", punchOutGeoFenceIds.filter((x: any) => x !== id));
    };

const handleSave = () => {
    if (mode === "add") {
        if (applyToAllUsers) {
            const totalItemsCount = Math.max(
                punchInGeoFenceIds.length,
                punchOutGeoFenceIds.length
            );
            const geoFencesPayloadArray = [];

            for (let i = 0; i < totalItemsCount; i++) {
                const inGeo = geoFencingArea.find(
                    (g: any) => g.Id === Number(punchInGeoFenceIds[i])
                );
                const outGeo = geoFencingArea.find(
                    (g: any) => g.Id === Number(punchOutGeoFenceIds[i])
                );

                geoFencesPayloadArray.push({
                    PunchInGeoFenceId: inGeo ? Number(inGeo.Id) : 0,                   
                    PunchOutGeoFenceId: outGeo ? Number(outGeo.Id) : 0,                    
                });
            }

            saveModal({
                mode: "add",
                payload: {
                    UserIds: [],
                    ApplyToAllUsers: true,
                    GeoFences: geoFencesPayloadArray,
                    Filters: filters,
                },
            });
        } else {
            const userIds = selectedIds.map(Number);

            const cleanInList = (geoFencingArea || []).filter((g: any) =>
                punchInGeoFenceIds.includes(g.Id)
            );

            const cleanOutList = (geoFencingArea || []).filter((g: any) =>
                punchOutGeoFenceIds.includes(g.Id)
            );

            const totalItemsCount = Math.max(
                cleanInList.length,
                cleanOutList.length
            );
            const geoFencesPayloadArray = [];

            for (let i = 0; i < totalItemsCount; i++) {
                const inItem = cleanInList[i];
                const outItem = cleanOutList[i];

                geoFencesPayloadArray.push({
                    PunchInGeoFenceId: inItem ? Number(inItem.Id) : 0,                   
                    PunchOutGeoFenceId: outItem ? Number(outItem.Id) : 0,                    
                });
            }

            saveModal({
                mode: "add",
                payload: {
                    UserIds: userIds,
                    ApplyToAllUsers: false,
                    GeoFences: geoFencesPayloadArray,
                },
            });
        }
    } else {
        const punchInGeoFences = (geoFencingArea || []).filter((g: any) =>
            punchInGeoFenceIds.includes(g.Id)
        );
        const punchOutGeoFences = (geoFencingArea || []).filter((g: any) =>
            punchOutGeoFenceIds.includes(g.Id)
        );

        const totalItemsCount = Math.max(
            punchInGeoFences.length,
            punchOutGeoFences.length
        );
        const geoFencesPayloadArray = [];

        for (let i = 0; i < totalItemsCount; i++) {
            const inItem = punchInGeoFences[i];
            const outItem = punchOutGeoFences[i];

            geoFencesPayloadArray.push({
                PunchInGeoFenceId: inItem ? Number(inItem.Id) : 0,
                PunchOutGeoFenceId: outItem ? Number(outItem.Id) : 0,
            });
        }

        saveModal({
            mode: "remove",
            payload: {
                UserIds: applyToAllUsers ? [] : selectedIds.map(Number),
                ApplyToAllUsers: applyToAllUsers,
                GeoFences: geoFencesPayloadArray,
                Filters: applyToAllUsers ? filters : null,
            },
        });
    }
};

const geofenceOptions = geoFencingArea?.map((item: any) => ({
    ...item,
    DisplayName: item.GeoFenceTypeName ? `${item.Name} (${item.GeoFenceTypeName})` : item.Name,
}));


useEffect(() => {
    if (modalOpen) {
        setSelectedAction(null);
        reset({
            punchInGeoFenceIds: [],
            punchOutGeoFenceIds: [],
        });
        dispatch(getGeoFencingArea());
    }
}, [modalOpen, reset, dispatch, selectedIds]);

const renderSelectedPreview = (items: any[], type: "PunchIn" | "PunchOut", onRemove: (id: any) => void) => {
    if (items.length === 0) return null;
    const styles = {
        PunchIn: { background: "#E1F5EE", color: "#0F6E56", border: "1px solid #0F6E56" },
        PunchOut: { background: "#FFF3E0", color: "#E65100", border: "1px solid #E65100" },
    };
    const activeStyle = styles[type];

    return (
        <div className="flex flex-wrap gap-2 mt-3 pt-2 border-t border-dashed border-gray-200">
            {items.map((item: any) => (
                <span
                    key={item.Id}
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        padding: "4px 10px",
                        borderRadius: "16px",
                        fontSize: "12px",
                        fontWeight: 500,
                        ...activeStyle,
                    }}
                >
                    {item.Name}
                    <button
                        type="button"
                        onClick={() => onRemove(item.Id)}
                        style={{
                            border: "none",
                            background: "transparent",
                            cursor: "pointer",
                            marginLeft: "6px",
                            fontWeight: "bold",
                            color: activeStyle.color,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "16px",
                            height: "16px",
                            borderRadius: "50%",
                        }}
                    >
                        ×
                    </button>
                </span>
            ))}
        </div>
    );
};

return (
    <Modal
        modalOpen={modalOpen}
        closeModal={closeModal}
        saveButtonText="Done"
        heading={mode === "add" ? "Add Geofences to Selected Users" : "Remove Geofences from Selected Users"}
        onSave={handleSave}
        isLoading={loading?.userGeofences}
    >
        <div className="p-10">
            <Alert severity="info" variant="standard" className="mb-6 rounded-lg">
                {`Select geofences to ${mode === "add" ? "assign" : "remove"} for the selected users. You can choose different geofences for Punch In and Punch Out.`}
            </Alert>
            <div className="flex items-center justify-between    gap-4 mt-6">

                <Button
                    variant={selectedAction === "PunchIn" ? "contained" : "outlined"}
                    onClick={() => setSelectedAction("PunchIn")}
                    // sx={{color:"#8dc572" ,borderColor:"#b8e2a4"}}

                    color="success"
                    startIcon={<LoginIcon sx={{ fontSize: 16 }} />}
                >
                    Punch In
                </Button>
                <Button
                    variant={selectedAction === "PunchOut" ? "contained" : "outlined"}
                    onClick={() => setSelectedAction("PunchOut")}
                    // sx={{color:"#be6464" ,borderColor:"#755424"}}
                    color="error"
                    startIcon={<LogoutIcon sx={{ fontSize: 16 }} />}
                >
                    Punch Out
                </Button>
            </div>
        </div>

        {selectedAction === "PunchIn" && (
            <div className="px-6 pb-4">
                <FormControl
                    name="punchInGeoFenceIds"
                    label={mode === "add" ? "Select Punch In Geofences To Add" : "Select Punch In Geofences To Remove"}
                    controlType="select"
                    control={control}
                    options={geofenceOptions || []}
                    idKey="Id"
                    nameKey="DisplayName"
                    isMulti={true}
                />
                {renderSelectedPreview(selectedPunchInGeofences, "PunchIn", handleRemovePunchIn)}
            </div>
        )}

        {selectedAction === "PunchOut" && (
            <div className="px-6 pb-4">
                <FormControl
                    name="punchOutGeoFenceIds"
                    label={mode === "add" ? "Select Punch Out Geofences To Add" : "Select Punch Out Geofences To Remove"}
                    controlType="select"
                    control={control}
                    options={geofenceOptions || []}
                    idKey="Id"
                    nameKey="DisplayName"
                    isMulti={true}
                />
                {renderSelectedPreview(selectedPunchOutGeofences, "PunchOut", handleRemovePunchOut)}
            </div>
        )}
    </Modal>
);
};

export default memo(ManageSelectedUsersGeofences);
